import {
    AccountMergeStatus,
    PreviewAccountChange,
    PreviewAccountOrigin,
} from "@common/types/domains/accounting/chartOfAccountsTemplateApply";
import type { PreviewExtendedAccount } from "./applyCoaTemplatePreviewMapper";
import type { CoaPreviewMode } from "./applyCoaPreviewTagCopy";
import {
    isActiveForMode,
    isArchivedForMode,
} from "./applyCoaPreviewArchivedState";

// Preview-local filter values for the apply-template preview. These mirror the
// diff data the backend now returns (origin + change flags + disabled state)
// and drive the preview's "Origin", "Updates", and "Source" filters.

// Origin: which source(s) a row comes from. The filter is a multi-select of the
// two sources — the Template and the entity being previewed. A matched account
// belongs to both. An empty selection means "all".
export enum ApplyCoaPreviewOriginSource {
    template = "template",
    entity = "entity",
}

// Updates: the material change (or archived state) the apply would surface.
// Split account/group per the memo. Renamed/Renumbered/Reassigned-group and
// Archived-group are intentionally omitted: the merge engine preserves user
// codes, renames only role-paired holders (shown by their new name), and
// never archives or moves existing groups, so there is no
// truthful data behind them. `moved` is an ordering-only change surfaced by the
// apply-time ordering pass (an existing account whose position shifts).
export enum ApplyCoaPreviewUpdate {
    all = "all",
    addedAccount = "addedAccount",
    addedGroup = "addedGroup",
    reassignedAccount = "reassignedAccount",
    moved = "moved",
    active = "active",
    archived = "archived",
}

// Source. Custom = isManuallyCreated, Kick = !isManuallyCreated (mirrors the
// live view's Source column).
export enum ApplyCoaPreviewSource {
    all = "all",
    custom = "custom",
    kick = "kick",
}

// Multi-select over the two sources, exclusive: "Template" shows accounts that
// exist ONLY in the template (new), "Entity" shows accounts that exist ONLY on
// the entity (not in the template). Selecting both shows both diff sets. Matched
// (unchanged, on both) accounts are the common base and appear only in the
// default, unfiltered view. An empty selection means "all".
export function matchesOrigin(
    account: PreviewExtendedAccount,
    sources: ApplyCoaPreviewOriginSource[],
): boolean {
    if (sources.length === 0) {
        return true;
    }
    return sources.some((source) =>
        source === ApplyCoaPreviewOriginSource.template
            ? account.origin === PreviewAccountOrigin.templateOnly
            : account.origin === PreviewAccountOrigin.entityOnly,
    );
}

// Multi-select over the update filters. An empty selection means "all"; a
// non-empty selection matches when the account satisfies ANY selected update
// (mirrors matchesOrigin's OR semantics).
export function matchesUpdate({
    account,
    updates,
    newGroupIds,
    mode = "preview",
}: {
    account: PreviewExtendedAccount;
    updates: ApplyCoaPreviewUpdate[];
    newGroupIds: Set<string>;
    mode?: CoaPreviewMode;
}): boolean {
    if (updates.length === 0) {
        return true;
    }
    return updates.some((update) =>
        matchesSingleUpdate({ account, update, newGroupIds, mode }),
    );
}

function matchesSingleUpdate({
    account,
    update,
    newGroupIds,
    mode,
}: {
    account: PreviewExtendedAccount;
    update: ApplyCoaPreviewUpdate;
    newGroupIds: Set<string>;
    mode: CoaPreviewMode;
}): boolean {
    switch (update) {
        case ApplyCoaPreviewUpdate.addedAccount:
            return account.changes.includes(PreviewAccountChange.added);
        case ApplyCoaPreviewUpdate.addedGroup:
            return (
                account.parentGroupId !== null &&
                newGroupIds.has(account.parentGroupId)
            );
        case ApplyCoaPreviewUpdate.reassignedAccount:
            return account.changes.includes(PreviewAccountChange.reassigned);
        case ApplyCoaPreviewUpdate.moved:
            return account.mergeStatus === AccountMergeStatus.moved;
        case ApplyCoaPreviewUpdate.active:
            return isActiveForMode(account, mode);
        case ApplyCoaPreviewUpdate.archived:
            // Mirror the "Archived" tag driver so tag and filter never diverge.
            return isArchivedForMode(account, mode);
        case ApplyCoaPreviewUpdate.all:
        default:
            return true;
    }
}

export function matchesSource(
    account: PreviewExtendedAccount,
    source: ApplyCoaPreviewSource,
): boolean {
    switch (source) {
        case ApplyCoaPreviewSource.custom:
            return account.isManuallyCreated;
        case ApplyCoaPreviewSource.kick:
            return !account.isManuallyCreated;
        case ApplyCoaPreviewSource.all:
        default:
            return true;
    }
}
